import { getGridStringOperators } from '@mui/x-data-grid';
import { getHours, getMinutes, isValid } from 'date-fns';
import reduce from 'lodash/reduce';

export const optionsToLabelMap = <T extends { value: number; label: unknown }>(
  options: T[]
): Record<number, string> =>
  reduce(
    options,
    (acc: Record<number, string>, o) => {
      acc[o.value] = String(o.label ?? '');
      return acc;
    },
    {}
  );

const toMinutesSinceMidnight = (v: string | null | undefined): number => {
  if (!v) return -1;
  const d = new Date(v);
  return isValid(d) ? getHours(d) * 60 + getMinutes(d) : -1;
};

export const isoTimeOnlySortComparator = (
  v1: string | null | undefined,
  v2: string | null | undefined
): number => toMinutesSinceMidnight(v1) - toMinutesSinceMidnight(v2);

export const isoDateSortComparator = (
  v1: string | null | undefined,
  v2: string | null | undefined
): number => new Date(v1 ?? 0).getTime() - new Date(v2 ?? 0).getTime();

export const makeLabelSortComparator =
  (labelMap: Record<number, string>) =>
  (v1: number, v2: number): number =>
    (labelMap[v1] ?? '').localeCompare(labelMap[v2] ?? '');

/**
 * Wraps MUI's built-in string filter operators so each one filters against
 * the formatted display value (e.g. "Sun 09/27/2026") rather than the raw
 * field value (e.g. an ISO date string). Sort is unaffected.
 */
export const makeFormattedDateFilterOperators = (
  formatter: (v: string) => string
) =>
  getGridStringOperators().map((op) => ({
    ...op,
    getApplyFilterFn: (filterItem: any, column: any) => {
      const fn = op.getApplyFilterFn(filterItem, column);
      if (!fn) return null;
      return (value: string, row: any, col: any, apiRef: any) =>
        fn(formatter(value ?? ''), row, col, apiRef);
    },
  }));
